/**
 * Project detail — the operator's workspace for one site.
 *
 * Layout:
 *   - PageHeader with project meta + actions
 *   - Hero row: health score ring + project-wide stat tiles + integration status
 *   - Critical alerts callouts (missing connections, failures, pending approvals)
 *   - Tabs: Overview · Audits · Keywords · Tracking · Competitors · Briefs ·
 *           Calendars · Campaigns · Forecasts · Reports · Integrations
 *   - Rank / GSC also available under Strategy → Tracking (/tracking)
 *
 * Every service tab follows the same template:
 *   1. Stats row (4 StatTiles): Total / In-progress / Failed (7d) / Domain metric
 *   2. Optional "Latest result" summary card
 *   3. "All items" list with create button in the toolbar
 *
 * Anti-goals: duplicate metrics across sections, vanity counters, ad-hoc
 * markup per service.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Link,
  useNavigate,
  useParams,
  useRouterState,
  useSearch,
} from '@tanstack/react-router';
import { motion } from 'framer-motion';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  Activity,
  AlertOctagon,
  AlertTriangle,
  ArrowRight,
  CalendarDays,
  CheckCircle2,
  CircleDot,
  Clock,
  Download,
  FileSearch,
  Gauge,
  Globe,
  KeyRound,
  LineChart as LineChartIcon,
  ListTree,
  MoreHorizontal,
  Notebook,
  Play,
  Plug,
  Search,
  Sparkles,
  Target,
  Trash2,
  TrendingUp,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import type {
  Audit,
  Brief,
  Campaign,
  CompetitorAnalysis,
  ContentCalendar,
  Forecast,
  KeywordRun,
  Project,
  Report,
} from '@seo/types';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { Page, PageHeader } from '@/components/ui/page';
import { Surface } from '@/components/ui/surface';
import {
  Section,
  SectionHeader,
  SectionTitle,
  SectionDescription,
  SectionToolbar,
} from '@/components/ui/section';
import { StatTile } from '@/components/ui/stat-tile';
import { Callout } from '@/components/ui/callout';
import { EmptyState } from '@/components/ui/empty';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { TextAreaField, TextField, FormField } from '@/components/ui/form-field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ErrorBanner } from '@/components/error-banner';
import { Spinner } from '@/components/spinner';
import { StatusPill, humanStatus } from '@/components/status-pill';
import { ScoreRing } from '@/components/score-ring';
import { fadeUp } from '@/components/motion';
import { BriefCreateButton } from '@/features/briefs/components/brief-create-button';
import { CompetitorCreateButton } from '@/features/competitors/components/competitor-create-button';
import { CompetitorRunButton } from '@/features/competitors/components/competitor-run-button';
import { CalendarCreateButton } from '@/features/calendars/components/calendar-create-button';
import { CampaignCreateButton } from '@/features/campaigns/components/campaign-create-button';
import { ForecastCreateButton } from '@/features/forecasts/components/forecast-create-button';
import { GoogleIntegrationCard } from '@/components/google-integration-card';
import { useAuthStore } from '@/lib/auth';
import { downloadAuthed } from '@/lib/download';
import { apiDelete, summarizeApiError } from '@/lib/api';
import { hostname, relTime } from '@/lib/format';
import {
  countByStatus,
  isFailed,
  isInProgress,
  isNeedsReview,
  isSuccess,
} from '@/lib/status';
import { cn } from '@/lib/cn';

import { useProject } from '@/features/projects/use-project';
import { useUpdateProject } from '@/features/projects/use-update-project';
import { useAudits } from '@/features/audits/use-audits';
import { CreateAuditDialog } from '@/features/audits/create-audit-dialog';
import { useKeywordRuns } from '@/features/keywords/use-keyword-runs';
import { useCreateKeywordRun } from '@/features/keywords/use-create-keyword-run';
import { useBriefs } from '@/features/briefs/use-briefs';
import { useReports } from '@/features/reports/use-reports';
import { useRunReport } from '@/features/reports/use-run-report';
import { useCompetitors } from '@/features/competitors/use-competitors';
import { useCompetitorRuns } from '@/features/competitors/use-competitor-runs';
import { useCompetitorAnalyses } from '@/features/competitor-analyses/use-competitor-analyses';
import { CompetitorAnalysisCreateButton } from '@/features/competitor-analyses/components/competitor-analysis-create-button';
import { useCalendars } from '@/features/calendars/use-calendars';
import { useCampaigns } from '@/features/campaigns/use-campaigns';
import { useForecasts } from '@/features/forecasts/use-forecasts';
import { useGoogleIntegrationStatus } from '@/features/integrations/use-google';
import { useTrackedKeywords } from '@/features/ranks/use-tracked-keywords';
import { TrackingPanel } from '@/features/ranks/components/tracking-panel';

const COUNTRIES = ['US', 'IN', 'GB', 'DE', 'CA', 'AU'] as const;
const LANGUAGES = ['en', 'hi', 'de', 'fr', 'es'] as const;

/* ------------------------------------------------------------------ */
/* Helpers                                                              */
/* ------------------------------------------------------------------ */

function sortNewest<T extends { created_at: string }>(items: readonly T[]): T[] {
  return [...items].sort((a, b) =>
    (b.created_at ?? '').localeCompare(a.created_at ?? ''),
  );
}

function latestOf<T extends { created_at: string }>(
  items: readonly T[],
): T | null {
  return sortNewest(items)[0] ?? null;
}

function latestSuccessOf<T extends { created_at: string; status: string }>(
  items: readonly T[],
): T | null {
  return sortNewest(items).find((it) => isSuccess(it.status)) ?? null;
}

function healthScoreTone(
  score: number | null | undefined,
): 'default' | 'success' | 'warning' | 'danger' {
  if (score == null) return 'default';
  if (score >= 80) return 'success';
  if (score >= 60) return 'default';
  if (score >= 40) return 'warning';
  return 'danger';
}

function seedLabel(seed: KeywordRun['seed']): string {
  if (seed.type === 'domain') return hostname(seed.value);
  if (seed.type === 'topics') {
    const head = seed.value.slice(0, 3).join(', ');
    return seed.value.length > 3 ? `${head}…` : head;
  }
  return seed.value;
}

/* ------------------------------------------------------------------ */
/* Project detail                                                       */
/* ------------------------------------------------------------------ */

export function ProjectDetailRoute(): JSX.Element {
  const { projectId } = useParams({ strict: false }) as { projectId: string };

  // All hooks must run unconditionally on every render — keep them above any
  // early returns. React's hook ordering depends on it.
  const project = useProject(projectId);
  const audits = useAudits(projectId);
  const keywords = useKeywordRuns(projectId);
  const briefs = useBriefs(projectId);
  const reports = useReports(projectId);
  const competitorRuns = useCompetitorRuns(projectId);
  const calendars = useCalendars(projectId);
  const campaigns = useCampaigns(projectId);
  const forecasts = useForecasts(projectId);
  const googleStatus = useGoogleIntegrationStatus(projectId);

  const auditItems = audits.data?.items ?? [];
  const briefItems = briefs.data?.items ?? [];
  const reportItems = reports.data?.items ?? [];
  const campaignItems = campaigns.data?.items ?? [];

  const latestAudit = latestSuccessOf(auditItems) ?? latestOf(auditItems);
  const latestReport = latestSuccessOf(reportItems);

  const integrationConnected = !!googleStatus.data?.connected;
  const integrationConfigured = !!googleStatus.data?.configured;
  const integrationComplete =
    integrationConnected &&
    !!googleStatus.data?.ga4_property_id &&
    !!googleStatus.data?.gsc_site_url;

  const projectIdForAlerts = project.data?.id ?? projectId;

  // Cross-service alerts for this project
  const alerts: ProjectAlert[] = useMemo(() => {
    const out: ProjectAlert[] = [];

    // Pending campaign approvals
    for (const c of campaignItems.filter((c) => isNeedsReview(c.status))) {
      out.push({
        id: `pending-${c.id}`,
        tone: 'warning',
        title: 'Campaign awaiting your approval',
        description: c.goal.slice(0, 140),
        to: '/campaigns/$campaignId',
        params: { campaignId: c.id },
        actionLabel: 'Review',
      });
    }

    // Recent failures across all services
    const cutoff = Date.now() - 7 * 86400_000;
    const failed = [
      ...auditItems,
      ...briefItems,
      ...reportItems,
      ...(competitorRuns.data?.items ?? []),
      ...campaignItems,
      ...(forecasts.data?.items ?? []),
      ...(keywords.data?.items ?? []),
      ...(calendars.data?.items ?? []),
    ].filter(
      (it) =>
        isFailed(it.status) && new Date(it.created_at).getTime() >= cutoff,
    );
    if (failed.length > 0) {
      out.push({
        id: 'failed',
        tone: 'danger',
        title: `${failed.length} failed run${failed.length === 1 ? '' : 's'} in the last 7 days`,
        description:
          'Open each tab to see the failure reason, then re-run with adjusted inputs.',
      });
    }

    // Missing Google integration for projects that have reports
    if (
      reportItems.length > 0 &&
      integrationConfigured &&
      !integrationComplete
    ) {
      out.push({
        id: 'google',
        tone: 'warning',
        title: 'Reports are missing traffic data',
        description:
          'Connect Google Analytics + Search Console to unlock sessions, clicks, and impressions.',
        to: '/projects/$projectId',
        params: { projectId: projectIdForAlerts },
        hash: 'integrations',
        actionLabel: 'Connect',
      });
    }

    // No audit ever
    if (auditItems.length === 0 && !audits.isLoading) {
      out.push({
        id: 'no-audit',
        tone: 'info',
        title: 'No audit yet',
        description:
          'Run an audit to establish a baseline health score and surface fixable issues.',
        to: '/projects/$projectId',
        params: { projectId: projectIdForAlerts },
        hash: 'audits',
        actionLabel: 'Run audit',
      });
    }

    return out;
  }, [
    auditItems,
    briefItems,
    reportItems,
    campaignItems,
    competitorRuns.data,
    forecasts.data,
    keywords.data,
    calendars.data,
    integrationConfigured,
    integrationComplete,
    projectIdForAlerts,
    audits.isLoading,
  ]);

  // ---- Early returns AFTER all hooks ----
  if (project.error) return <ErrorBanner error={project.error} />;
  if (project.isLoading || !project.data) {
    return (
      <div className="flex justify-center p-10">
        <Spinner />
      </div>
    );
  }

  const p = project.data;
  const host = hostname(p.domain);

  return (
    <Page>
      <PageHeader
        eyebrow="Project"
        title={p.name}
        description={host}
        actions={
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>{p.name}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => toast.info('Delete project — coming soon')}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="mr-2 h-4 w-4" /> Delete project
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        }
      />

      {/* Project hero — health score + key project-wide signals */}
      <motion.div variants={fadeUp}>
        <ProjectHero
          project={p}
          latestAudit={latestAudit}
          latestReport={latestReport}
          integrationConnected={integrationConnected}
          integrationComplete={integrationComplete}
        />
      </motion.div>

      {/* Critical alerts */}
      {alerts.length > 0 ? (
        <motion.div variants={fadeUp} className="space-y-2">
          {alerts.map((alert) => (
            <Callout
              key={alert.id}
              tone={alert.tone}
              title={alert.title}
              action={
                alert.to ? (
                  <Button
                    asChild
                    size="sm"
                    variant={alert.tone === 'danger' ? 'default' : 'outline'}
                  >
                    <Link
                      to={alert.to}
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      params={alert.params as any}
                      hash={alert.hash}
                    >
                      {alert.actionLabel}
                    </Link>
                  </Button>
                ) : undefined
              }
            >
              {alert.description}
            </Callout>
          ))}
        </motion.div>
      ) : null}

      <ProjectTabs projectId={projectId}>
        <TabsList className="w-full justify-start overflow-x-auto">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="audits">Audits</TabsTrigger>
          <TabsTrigger value="keywords">Keywords</TabsTrigger>
          <TabsTrigger value="tracking">Tracking</TabsTrigger>
          <TabsTrigger value="competitors">Competitors</TabsTrigger>
          <TabsTrigger value="briefs">Briefs</TabsTrigger>
          <TabsTrigger value="calendars">Calendars</TabsTrigger>
          <TabsTrigger value="campaigns">Campaigns</TabsTrigger>
          <TabsTrigger value="forecasts">Forecasts</TabsTrigger>
          <TabsTrigger value="reports">Reports</TabsTrigger>
          <TabsTrigger value="integrations">Integrations</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <OverviewPanel projectId={projectId} project={p} />
        </TabsContent>

        <TabsContent value="audits">
          <AuditsPanel projectId={projectId} domain={p.domain} />
        </TabsContent>

        <TabsContent value="keywords">
          <KeywordsPanel projectId={projectId} domain={p.domain} />
        </TabsContent>

        <TabsContent value="tracking">
          <TrackingPanel projectId={projectId} project={p} />
        </TabsContent>

        <TabsContent value="competitors">
          <CompetitorsPanel projectId={projectId} project={p} />
        </TabsContent>

        <TabsContent value="briefs">
          <BriefsPanel projectId={projectId} />
        </TabsContent>

        <TabsContent value="calendars">
          <CalendarsPanel projectId={projectId} project={p} />
        </TabsContent>

        <TabsContent value="campaigns">
          <CampaignsPanel projectId={projectId} project={p} />
        </TabsContent>

        <TabsContent value="forecasts">
          <ForecastsPanel projectId={projectId} project={p} />
        </TabsContent>

        <TabsContent value="reports">
          <ReportsPanel projectId={projectId} />
        </TabsContent>

        <TabsContent value="settings">
          <SettingsPanel project={p} />
        </TabsContent>

        <TabsContent value="integrations">
          <div className="space-y-4">
            <GoogleIntegrationCard projectId={projectId} />
            <Callout tone="info" title="Where to see Search Console results">
              Opportunities, content decay, and keyword positions live on this
              project’s{' '}
              <Link
                to="/projects/$projectId"
                params={{ projectId }}
                hash="tracking"
                className="font-medium underline underline-offset-2"
              >
                Tracking
              </Link>{' '}
              tab (also under Strategy → Tracking). This tab is only for connecting
              Google.
            </Callout>
          </div>
        </TabsContent>
      </ProjectTabs>
    </Page>
  );
}

/* ------------------------------------------------------------------ */
/* Project hero — health score + integration status                     */
/* ------------------------------------------------------------------ */

function ProjectHero({
  project: _project,
  latestAudit,
  latestReport,
  integrationConnected,
  integrationComplete,
}: {
  project: Project;
  latestAudit: Audit | null;
  latestReport: Report | null;
  integrationConnected: boolean;
  integrationComplete: boolean;
}): JSX.Element {
  const auditScore = latestAudit?.health_score ?? null;
  const tone = healthScoreTone(auditScore);
  const tierLabel =
    auditScore === null
      ? 'No audit yet'
      : auditScore >= 80
        ? 'Excellent'
        : auditScore >= 60
          ? 'Healthy'
          : auditScore >= 40
            ? 'Needs work'
            : 'Critical';

  const integrationTone = integrationComplete
    ? 'success'
    : integrationConnected
      ? 'warning'
      : 'danger';
  const integrationLabel = integrationComplete
    ? 'Connected'
    : integrationConnected
      ? 'Partial'
      : 'Not connected';

  return (
    <div className="grid gap-4 lg:grid-cols-[300px_minmax(0,1fr)]">
      {/* Health score card */}
      <Surface tone="raised" className="flex items-center gap-4 p-5">
        {auditScore !== null ? (
          <ScoreRing score={auditScore} size={120} />
        ) : (
          <div className="flex h-[120px] w-[120px] flex-col items-center justify-center rounded-full border border-dashed border-border/70">
            <span className="font-mono text-3xl text-muted-foreground">—</span>
            <span className="mt-1 text-[10px] uppercase tracking-wider text-muted-foreground">
              no audit
            </span>
          </div>
        )}
        <div className="min-w-0 flex-1 space-y-2">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Site health
          </div>
          <div
            className={cn(
              'text-xl font-semibold',
              tone === 'success' && 'text-success',
              tone === 'warning' && 'text-warning',
              tone === 'danger' && 'text-destructive',
            )}
          >
            {tierLabel}
          </div>
          <div className="text-xs text-muted-foreground">
            {latestAudit
              ? `Last audit ${relTime(latestAudit.created_at)}`
              : 'Run an audit to get a baseline.'}
          </div>
        </div>
      </Surface>

      {/* Stat strip */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <StatTile
          icon={Plug}
          tone={integrationTone}
          label="Integrations"
          value={integrationLabel}
          hint={integrationComplete ? 'GA4 + GSC linked' : 'GA4/GSC for reports'}
        />
        <StatTile
          icon={LineChartIcon}
          label="Reports"
          value={latestReport ? `${latestReport.cadence}` : '—'}
          hint={
            latestReport
              ? `Last ${relTime(latestReport.delivered_at ?? latestReport.created_at)}`
              : 'None delivered yet'
          }
        />
        <StatTile
          icon={FileSearch}
          label="Pages crawled"
          value={
            (latestAudit as Audit & { pages_crawled?: number })?.pages_crawled ??
            '—'
          }
          hint={latestAudit ? 'in latest audit' : 'no audit yet'}
        />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Tab state syncing                                                    */
/* ------------------------------------------------------------------ */

const TAB_KEYS = [
  'overview',
  'audits',
  'keywords',
  'tracking',
  'competitors',
  'briefs',
  'calendars',
  'campaigns',
  'forecasts',
  'reports',
  'integrations',
  'settings',
] as const;
type TabKey = (typeof TAB_KEYS)[number];

/**
 * Router-driven tab state. The active tab IS the URL hash — every switcher
 * (tab triggers, overview cards, alert links, breadcrumb tab crumbs) writes
 * it through the router, so they all behave identically: deep-linkable,
 * Back/Forward steps through tab changes, and in-page `<Link hash>` clicks
 * actually switch the tab (the old hand-rolled `hashchange` listener never
 * fired for router navigations, which made the alert CTAs dead clicks).
 */
function ProjectTabs({
  children,
}: {
  projectId: string;
  children: React.ReactNode;
}): JSX.Element {
  const navigate = useNavigate();
  const hash = useRouterState({ select: (s) => s.location.hash });
  const search = useSearch({ strict: false }) as {
    google?: 'connected' | 'denied';
  };

  const tab: TabKey = TAB_KEYS.includes(hash as TabKey)
    ? (hash as TabKey)
    : search.google
      ? 'integrations'
      : 'overview';

  // Google OAuth return leg: surface the outcome once, then strip the
  // param so refreshes don't re-open the Integrations tab forever.
  useEffect(() => {
    if (!search.google) return;
    if (search.google === 'connected') {
      toast.success('Google connected', {
        description: 'GA4 and Search Console access is now linked to this project.',
      });
    } else {
      toast.error('Google connection cancelled', {
        description: 'No access was granted. You can retry any time.',
      });
    }
    void navigate({
      search: {} as never,
      hash: 'integrations',
      replace: true,
    });
  }, [search.google, navigate]);

  const onChange = (v: string): void => {
    if (!TAB_KEYS.includes(v as TabKey)) return;
    // Push (not replace) so browser Back steps back through tab changes.
    void navigate({ hash: v, search: true as never });
  };

  return (
    <Tabs value={tab} onValueChange={onChange} className="space-y-4">
      {children}
    </Tabs>
  );
}

/* ------------------------------------------------------------------ */
/* Service overview cards (shared by Overview tab)                      */
/* ------------------------------------------------------------------ */

interface ServiceCardData {
  icon: LucideIcon;
  label: string;
  tabHash: string;
  total: number;
  inProgress: number;
  /** Headline metric/status string. */
  headline: string;
  /** Latest item status if any. */
  latestStatus?: string;
  /** When set, navigate to a real route instead of a project tab hash. */
  to?: '/tracking';
  search?: { project: string };
}

function ServiceCard({
  data,
  projectId,
}: {
  data: ServiceCardData;
  projectId: string;
}): JSX.Element {
  const { icon: Icon, label, tabHash, total, inProgress, headline, latestStatus, to, search } =
    data;
  const body = (
      <Surface
        interactive
        className="flex h-full flex-col gap-3 p-4 transition-transform group-hover:-translate-y-0.5"
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="flex h-7 w-7 items-center justify-center rounded-md bg-muted/60 text-muted-foreground">
              <Icon className="h-3.5 w-3.5" />
            </span>
            <span className="text-sm font-semibold text-foreground group-hover:text-primary">
              {label}
            </span>
          </div>
          <ArrowRight className="h-3.5 w-3.5 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
        </div>
        <div className="text-2xl font-semibold tabular leading-none">
          {total}
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {inProgress > 0 ? (
            <Badge variant="info" className="gap-1 font-mono">
              <CircleDot className="h-3 w-3 animate-pulse" />
              {inProgress} running
            </Badge>
          ) : latestStatus ? (
            <StatusPill status={latestStatus} />
          ) : (
            <span>—</span>
          )}
          <span className="truncate">{headline}</span>
        </div>
      </Surface>
  );

  if (to) {
    return (
      <Link to={to} search={search} className="block group">
        {body}
      </Link>
    );
  }

  // Router-driven hash link — ProjectTabs derives the active tab from
  // router state, so this both updates the URL and switches the tab.
  return (
    <Link
      to="/projects/$projectId"
      params={{ projectId }}
      hash={tabHash}
      className="block group"
    >
      {body}
    </Link>
  );
}

/* ------------------------------------------------------------------ */
/* Overview panel                                                       */
/* ------------------------------------------------------------------ */

function OverviewPanel({
  projectId,
  project: _project,
}: {
  projectId: string;
  project: Project;
}): JSX.Element {
  const audits = useAudits(projectId);
  const keywords = useKeywordRuns(projectId);
  const briefs = useBriefs(projectId);
  const reports = useReports(projectId);
  const competitorsQ = useCompetitors(projectId);
  const competitorRuns = useCompetitorRuns(projectId);
  const calendars = useCalendars(projectId);
  const campaigns = useCampaigns(projectId);
  const forecasts = useForecasts(projectId);
  const tracked = useTrackedKeywords(projectId);

  const auditItems = audits.data?.items ?? [];
  const keywordItems = keywords.data?.items ?? [];
  const briefItems = briefs.data?.items ?? [];
  const reportItems = reports.data?.items ?? [];
  const competitorItems = competitorsQ.data?.items ?? [];
  const competitorRunItems = competitorRuns.data?.items ?? [];
  const calendarItems = calendars.data?.items ?? [];
  const campaignItems = campaigns.data?.items ?? [];
  const forecastItems = forecasts.data?.items ?? [];
  const trackedItems = tracked.data?.items ?? [];

  const services: ServiceCardData[] = useMemo(() => {
    const auditCounts = countByStatus(auditItems);
    const latestAudit = latestOf(auditItems);
    const latestCompletedAudit = latestSuccessOf(auditItems);
    return [
      {
        icon: FileSearch,
        label: 'Audits',
        tabHash: 'audits',
        total: auditItems.length,
        inProgress: auditCounts.inProgress,
        latestStatus: latestAudit?.status,
        headline:
          latestCompletedAudit?.health_score != null
            ? `Score ${latestCompletedAudit.health_score} · ${relTime(latestCompletedAudit.created_at)}`
            : latestAudit
              ? relTime(latestAudit.created_at)
              : 'Run your first audit',
      },
      {
        icon: KeyRound,
        label: 'Keyword runs',
        tabHash: 'keywords',
        total: keywordItems.length,
        inProgress: countByStatus(keywordItems).inProgress,
        latestStatus: latestOf(keywordItems)?.status,
        headline: latestOf(keywordItems)
          ? `Last run ${relTime(latestOf(keywordItems)!.created_at)}`
          : 'No runs yet',
      },
      {
        icon: LineChartIcon,
        label: 'Tracking',
        tabHash: 'tracking',
        total: trackedItems.length,
        inProgress: 0,
        latestStatus: undefined,
        headline:
          trackedItems.length > 0
            ? `${trackedItems.length} keyword${trackedItems.length === 1 ? '' : 's'} tracked`
            : 'Ranks + Search Console',
      },
      {
        icon: Globe,
        label: 'Competitors',
        tabHash: 'competitors',
        total: competitorItems.length,
        inProgress: countByStatus(competitorRunItems).inProgress,
        latestStatus: latestOf(competitorRunItems)?.status,
        headline:
          competitorItems.length > 0
            ? `${competitorRunItems.length} scan${competitorRunItems.length === 1 ? '' : 's'} run`
            : 'No competitors tracked',
      },
      {
        icon: Notebook,
        label: 'Briefs',
        tabHash: 'briefs',
        total: briefItems.length,
        inProgress: countByStatus(briefItems).inProgress,
        latestStatus: latestOf(briefItems)?.status,
        headline: latestOf(briefItems)
          ? latestOf(briefItems)!.target_keyword
          : 'No briefs yet',
      },
      {
        icon: CalendarDays,
        label: 'Calendars',
        tabHash: 'calendars',
        total: calendarItems.length,
        inProgress: countByStatus(calendarItems).inProgress,
        latestStatus: latestOf(calendarItems)?.status,
        headline: latestOf(calendarItems)
          ? `${latestOf(calendarItems)!.period_months}-month plan`
          : 'No calendars yet',
      },
      {
        icon: Target,
        label: 'Campaigns',
        tabHash: 'campaigns',
        total: campaignItems.length,
        inProgress: countByStatus(campaignItems).inProgress,
        latestStatus: latestOf(campaignItems)?.status,
        headline: latestOf(campaignItems)
          ? latestOf(campaignItems)!.goal.slice(0, 60)
          : 'No campaigns yet',
      },
      {
        icon: TrendingUp,
        label: 'Forecasts',
        tabHash: 'forecasts',
        total: forecastItems.length,
        inProgress: countByStatus(forecastItems).inProgress,
        latestStatus: latestOf(forecastItems)?.status,
        headline: latestOf(forecastItems)
          ? `${latestOf(forecastItems)!.horizon_days}-day horizon`
          : 'No forecasts yet',
      },
      {
        icon: LineChartIcon,
        label: 'Reports',
        tabHash: 'reports',
        total: reportItems.length,
        inProgress: countByStatus(reportItems).inProgress,
        latestStatus: latestOf(reportItems)?.status,
        headline: latestOf(reportItems)
          ? `${latestOf(reportItems)!.period_start} → ${latestOf(reportItems)!.period_end}`
          : 'No reports yet',
      },
    ];
  }, [
    projectId,
    auditItems,
    keywordItems,
    trackedItems,
    competitorItems,
    competitorRunItems,
    briefItems,
    calendarItems,
    campaignItems,
    forecastItems,
    reportItems,
  ]);

  // Activity stream across all services
  const activity = useMemo(() => buildActivityStream({
    audits: auditItems,
    briefs: briefItems,
    keywords: keywordItems,
    reports: reportItems,
    campaigns: campaignItems,
    forecasts: forecastItems,
    competitorRuns: competitorRunItems,
  }).slice(0, 10), [
    auditItems, briefItems, keywordItems, reportItems,
    campaignItems, forecastItems, competitorRunItems,
  ]);

  const scoreSeries = useMemo(() => {
    return [...auditItems]
      .filter((a) => a.health_score !== null)
      .sort((a, b) => (a.created_at ?? '').localeCompare(b.created_at ?? ''))
      .slice(-10)
      .map((a) => ({
        date: new Date(a.created_at).toLocaleDateString(undefined, {
          month: 'short',
          day: 'numeric',
        }),
        score: a.health_score as number,
      }));
  }, [auditItems]);

  return (
    <div className="space-y-4">
      {/* Service overview grid */}
      <Section>
        <SectionHeader>
          <div>
            <SectionTitle>Services at a glance</SectionTitle>
            <SectionDescription>
              Click any service to jump straight into its tab.
            </SectionDescription>
          </div>
        </SectionHeader>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {services.map((s) => (
            <ServiceCard key={s.label} data={s} projectId={projectId} />
          ))}
        </div>
      </Section>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Activity */}
        <div className="lg:col-span-2">
          <Section>
            <SectionHeader>
              <div>
                <SectionTitle className="flex items-center gap-2">
                  <Activity className="h-4 w-4 text-muted-foreground" />
                  Recent activity
                </SectionTitle>
                <SectionDescription>
                  Last 10 events across every service in this project.
                </SectionDescription>
              </div>
            </SectionHeader>
            {activity.length === 0 ? (
              <EmptyState
                icon={Activity}
                title="Nothing happening yet"
                description="Once you run audits, generate briefs, or kick off campaigns, they'll show up here."
                variant="inline"
              />
            ) : (
              <ul className="divide-y divide-border/60">
                {activity.map((a) => (
                  <li key={a.id}>
                    <Link
                      to={a.to}
                      params={a.params}
                      className="group flex items-center gap-3 py-2.5 first:pt-0 last:pb-0 hover:text-primary"
                    >
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-muted/60 text-muted-foreground">
                        <a.icon className="h-3.5 w-3.5" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium">
                          {a.label}
                        </div>
                        <div className="text-[11px] text-muted-foreground">
                          {a.kindLabel}
                        </div>
                      </div>
                      <StatusPill status={a.status} />
                      <span className="hidden w-20 shrink-0 text-right text-xs text-muted-foreground tabular sm:inline">
                        {relTime(a.createdAt)}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </div>

        {/* Audit health trend */}
        <Section>
          <SectionHeader>
            <div>
              <SectionTitle className="flex items-center gap-2">
                <Gauge className="h-4 w-4 text-muted-foreground" />
                Audit health trend
              </SectionTitle>
              <SectionDescription>
                Health score across the last 10 audits.
              </SectionDescription>
            </div>
          </SectionHeader>
          {scoreSeries.length < 2 ? (
            <EmptyState
              icon={Gauge}
              title="Not enough data"
              description="Run at least 2 audits to see a trend."
              variant="inline"
            />
          ) : (
            <div className="h-40">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={scoreSeries}
                  margin={{ top: 8, right: 8, bottom: 0, left: -16 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(var(--border))"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="date"
                    fontSize={10}
                    tickLine={false}
                    axisLine={false}
                    stroke="hsl(var(--muted-foreground))"
                  />
                  <YAxis
                    domain={[0, 100]}
                    fontSize={10}
                    tickLine={false}
                    axisLine={false}
                    stroke="hsl(var(--muted-foreground))"
                  />
                  <Tooltip
                    contentStyle={{
                      background: 'hsl(var(--popover))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="score"
                    stroke="hsl(var(--primary))"
                    strokeWidth={2.5}
                    dot={{ r: 3, fill: 'hsl(var(--primary))' }}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </Section>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Activity stream helper                                               */
/* ------------------------------------------------------------------ */

interface ActivityItem {
  id: string;
  icon: LucideIcon;
  kindLabel: string;
  label: string;
  status: string;
  createdAt: string;
  to: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  params: any;
}

function buildActivityStream(d: {
  audits: Audit[];
  briefs: Brief[];
  keywords: KeywordRun[];
  reports: Report[];
  campaigns: Campaign[];
  forecasts: Forecast[];
  competitorRuns: Array<{ id: string; status: string; created_at: string; competitor_ids: string[] }>;
}): ActivityItem[] {
  const items: ActivityItem[] = [
    ...d.audits.map<ActivityItem>((a) => ({
      id: `audit-${a.id}`,
      icon: FileSearch,
      kindLabel: 'Audit',
      label:
        a.health_score !== null
          ? `Audit · score ${a.health_score}`
          : `Audit ${a.id.slice(0, 8)}`,
      status: a.status,
      createdAt: a.created_at,
      to: '/audits/$auditId',
      params: { auditId: a.id },
    })),
    ...d.briefs.map<ActivityItem>((b) => ({
      id: `brief-${b.id}`,
      icon: Notebook,
      kindLabel: 'Brief',
      label: b.target_keyword || `Brief ${b.id.slice(0, 8)}`,
      status: b.status,
      createdAt: b.created_at,
      to: '/briefs/$briefId',
      params: { briefId: b.id },
    })),
    ...d.keywords.map<ActivityItem>((k) => ({
      id: `kw-${k.id}`,
      icon: KeyRound,
      kindLabel: 'Keyword run',
      label: seedLabel(k.seed),
      status: k.status,
      createdAt: k.created_at,
      to: '/keywords/$keywordRunId',
      params: { keywordRunId: k.id },
    })),
    ...d.reports.map<ActivityItem>((r) => ({
      id: `report-${r.id}`,
      icon: LineChartIcon,
      kindLabel: 'Report',
      label: `${r.cadence} · ${r.period_start} → ${r.period_end}`,
      status: r.status,
      createdAt: r.created_at,
      to: '/reports/$reportId',
      params: { reportId: r.id },
    })),
    ...d.campaigns.map<ActivityItem>((c) => ({
      id: `camp-${c.id}`,
      icon: Target,
      kindLabel: 'Campaign',
      label: c.goal.slice(0, 80),
      status: c.status,
      createdAt: c.created_at,
      to: '/campaigns/$campaignId',
      params: { campaignId: c.id },
    })),
    ...d.forecasts.map<ActivityItem>((f) => ({
      id: `fc-${f.id}`,
      icon: TrendingUp,
      kindLabel: 'Forecast',
      label: `${f.horizon_days}-day forecast`,
      status: f.status,
      createdAt: f.created_at,
      to: '/forecasts/$forecastId',
      params: { forecastId: f.id },
    })),
    ...d.competitorRuns.map<ActivityItem>((c) => ({
      id: `cr-${c.id}`,
      icon: Globe,
      kindLabel: 'Competitor scan',
      label: `${c.competitor_ids.length} competitor${c.competitor_ids.length === 1 ? '' : 's'}`,
      status: c.status,
      createdAt: c.created_at,
      to: '/competitors/$runId',
      params: { runId: c.id },
    })),
  ];
  return items.sort((a, b) =>
    (b.createdAt ?? '').localeCompare(a.createdAt ?? ''),
  );
}

/* ------------------------------------------------------------------ */
/* Standardized stats row                                               */
/* ------------------------------------------------------------------ */

function StatsRow({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">{children}</div>
  );
}

/* ================================================================== */
/* SERVICE PANELS                                                       */
/* ================================================================== */
/*
 * Each panel follows the same template:
 *   1. <StatsRow> with 4 <StatTile>s
 *   2. Optional <Section> "Latest result" summary
 *   3. <Section> "All items" list with create button in toolbar
 */

interface ProjectAlert {
  id: string;
  tone: 'info' | 'warning' | 'danger';
  title: string;
  description: string;
  to?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  params?: any;
  hash?: string;
  actionLabel?: string;
}

/* ---------------- Audits ---------------- */

function AuditsPanel({
  projectId,
  domain,
}: {
  projectId: string;
  domain: string;
}): JSX.Element {
  const audits = useAudits(projectId);
  const [createOpen, setCreateOpen] = useState(false);

  const items = audits.data?.items ?? [];
  const counts = countByStatus(items);
  const latestCompleted = latestSuccessOf(items);
  const failedRecent = items.filter(
    (a) =>
      isFailed(a.status) &&
      Date.now() - new Date(a.created_at).getTime() < 7 * 86400_000,
  ).length;

  return (
    <div className="space-y-4">
      <CreateAuditDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        defaultProjectId={projectId}
      />
      <StatsRow>
        <StatTile label="Total audits" value={counts.total} icon={FileSearch} />
        <StatTile
          label="In progress"
          value={counts.inProgress}
          icon={CircleDot}
          tone={counts.inProgress > 0 ? 'info' : 'muted'}
        />
        <StatTile
          label="Failed · 7d"
          value={failedRecent}
          icon={AlertOctagon}
          tone={failedRecent > 0 ? 'danger' : 'muted'}
        />
        <StatTile
          label="Latest score"
          value={latestCompleted?.health_score ?? '—'}
          tone={healthScoreTone(latestCompleted?.health_score)}
          icon={Gauge}
          hint={
            latestCompleted ? relTime(latestCompleted.created_at) : 'no result'
          }
        />
      </StatsRow>

      {latestCompleted ? (
        <Section>
          <SectionHeader>
            <div>
              <SectionTitle>Latest completed audit</SectionTitle>
              <SectionDescription>
                Health score breakdown and key warnings.
              </SectionDescription>
            </div>
            <SectionToolbar>
              <Button asChild variant="outline" size="sm">
                <Link
                  to="/audits/$auditId"
                  params={{ auditId: latestCompleted.id }}
                >
                  Open audit
                </Link>
              </Button>
            </SectionToolbar>
          </SectionHeader>
          <div className="grid gap-4 sm:grid-cols-[140px_minmax(0,1fr)]">
            <div className="flex justify-center sm:justify-start">
              {latestCompleted.health_score !== null ? (
                <ScoreRing score={latestCompleted.health_score} size={120} />
              ) : null}
            </div>
            <div className="space-y-3">
              {latestCompleted.sub_scores ? (
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
                  {(
                    [
                      ['technical', 'Technical'],
                      ['on_page', 'On-page'],
                      ['performance', 'Perf'],
                      ['content', 'Content'],
                      ['indexing', 'Index'],
                    ] as const
                  ).map(([key, label]) => (
                    <Surface
                      key={key}
                      tone="muted"
                      className="px-3 py-2 text-center"
                    >
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                        {label}
                      </div>
                      <div className="mt-0.5 font-mono text-lg tabular">
                        {latestCompleted.sub_scores![key]}
                      </div>
                    </Surface>
                  ))}
                </div>
              ) : null}
              {latestCompleted.warnings.length > 0 ? (
                <Callout tone="warning" title="Warnings on this audit">
                  {latestCompleted.warnings.length} warning
                  {latestCompleted.warnings.length === 1 ? '' : 's'} —{' '}
                  {latestCompleted.warnings[0].message}
                </Callout>
              ) : null}
            </div>
          </div>
        </Section>
      ) : null}

      <Section>
        <SectionHeader>
          <div>
            <SectionTitle>All audits</SectionTitle>
            <SectionDescription>
              Crawls, technical + on-page checks, and health scoring.
            </SectionDescription>
          </div>
          <SectionToolbar>
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              <Play className="h-4 w-4 fill-current" />
              Start audit
            </Button>
          </SectionToolbar>
        </SectionHeader>

        {audits.isLoading ? (
          <div className="flex justify-center p-6">
            <Spinner />
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={FileSearch}
            title="No audits yet"
            description="Start your first audit to see findings and a health score."
            variant="inline"
            action={
              <Button size="sm" onClick={() => setCreateOpen(true)}>
                <Play className="h-4 w-4 fill-current" /> Start audit
              </Button>
            }
          />
        ) : (
          <ul className="space-y-2">
            {sortNewest(items).map((a) => {
              const crawled = (a as Audit & { pages_crawled?: number })
                .pages_crawled;
              return (
                <li key={a.id}>
                  <Link
                    to="/audits/$auditId"
                    params={{ auditId: a.id }}
                    className="group flex items-center justify-between gap-3 rounded-md border border-border/70 bg-card p-3 transition-colors hover:border-primary/40 hover:bg-accent/30"
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <StatusPill status={a.status} />
                      <span className="truncate text-sm font-medium text-foreground group-hover:text-primary">
                        {domain}
                      </span>
                      {a.health_score !== null ? (
                        <Badge variant="outline" className="font-mono text-[10px]">
                          Score {a.health_score}
                        </Badge>
                      ) : null}
                      {typeof crawled === 'number' ? (
                        <Badge variant="outline" className="font-mono text-[10px]">
                          {crawled} {crawled === 1 ? 'page' : 'pages'} crawled
                        </Badge>
                      ) : null}
                    </div>
                    <span className="shrink-0 text-xs text-muted-foreground tabular">
                      {relTime(a.started_at ?? a.created_at)}
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </Section>
    </div>
  );
}

/* ---------------- Keywords ---------------- */

function KeywordsPanel({
  projectId,
  domain,
}: {
  projectId: string;
  domain: string;
}): JSX.Element {
  const runs = useKeywordRuns(projectId);
  const create = useCreateKeywordRun();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const items = runs.data?.items ?? [];
  const counts = countByStatus(items);
  const latest = latestOf(items);
  const failedRecent = items.filter(
    (r) =>
      isFailed(r.status) &&
      Date.now() - new Date(r.created_at).getTime() < 7 * 86400_000,
  ).length;

  return (
    <div className="space-y-4">
      <StatsRow>
        <StatTile label="Total runs" value={counts.total} icon={KeyRound} />
        <StatTile
          label="In progress"
          value={counts.inProgress}
          icon={CircleDot}
          tone={counts.inProgress > 0 ? 'info' : 'muted'}
        />
        <StatTile
          label="Failed · 7d"
          value={failedRecent}
          icon={AlertOctagon}
          tone={failedRecent > 0 ? 'danger' : 'muted'}
        />
        <StatTile
          label="Latest"
          value={latest ? latest.country_code : '—'}
          icon={Clock}
          hint={latest ? relTime(latest.created_at) : 'no runs yet'}
        />
      </StatsRow>

      {latest ? (
        <Section>
          <SectionHeader>
            <div>
              <SectionTitle>Latest keyword run</SectionTitle>
              <SectionDescription>
                Seed, locale and current status.
              </SectionDescription>
            </div>
            <SectionToolbar>
              <Button asChild variant="outline" size="sm">
                <Link
                  to="/keywords/$keywordRunId"
                  params={{ keywordRunId: latest.id }}
                >
                  Open run
                </Link>
              </Button>
            </SectionToolbar>
          </SectionHeader>
          <div className="flex flex-wrap items-center gap-3">
            <StatusPill status={latest.status} />
            <Badge variant="outline" className="font-mono">
              Seed · {seedLabel(latest.seed)}
            </Badge>
            <Badge variant="outline" className="font-mono">
              {latest.country_code} / {latest.language}
            </Badge>
            <span className="text-xs text-muted-foreground">
              started {relTime(latest.created_at)}
            </span>
          </div>
        </Section>
      ) : null}

      <Section>
        <SectionHeader>
          <div>
            <SectionTitle>All keyword runs</SectionTitle>
            <SectionDescription>Expand, classify, cluster.</SectionDescription>
          </div>
          <SectionToolbar>
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <Button size="sm">
                  <Play className="h-4 w-4 fill-current" /> New keyword run
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80">
                <NewKeywordRunForm
                  projectId={projectId}
                  defaultSeed={domain}
                  onCreated={(id) => {
                    setOpen(false);
                    void navigate({
                      to: '/keywords/$keywordRunId',
                      params: { keywordRunId: id },
                    });
                  }}
                  creating={create.isPending}
                  onSubmit={(values) => create.mutateAsync(values)}
                />
              </PopoverContent>
            </Popover>
          </SectionToolbar>
        </SectionHeader>

        {runs.isLoading ? (
          <div className="flex justify-center p-6">
            <Spinner />
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={KeyRound}
            title="No keyword runs yet"
            description="Start a run to expand, score, and cluster keywords for this project."
            variant="inline"
            action={
              <Button size="sm" onClick={() => setOpen(true)}>
                <Play className="h-4 w-4 fill-current" /> New keyword run
              </Button>
            }
          />
        ) : (
          <ul className="space-y-2">
            {sortNewest(items).map((r) => (
              <li key={r.id}>
                <Link
                  to="/keywords/$keywordRunId"
                  params={{ keywordRunId: r.id }}
                  className="group flex items-center justify-between gap-3 rounded-md border border-border/70 bg-card p-3 transition-colors hover:border-primary/40 hover:bg-accent/30"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <StatusPill status={r.status} />
                    <span className="truncate text-sm font-medium text-foreground group-hover:text-primary">
                      {seedLabel(r.seed)}
                    </span>
                    <Badge variant="outline" className="font-mono text-[10px]">
                      {r.country_code} / {r.language}
                    </Badge>
                  </div>
                  <span className="shrink-0 text-xs text-muted-foreground tabular">
                    {relTime(r.created_at)}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}

function NewKeywordRunForm({
  projectId,
  defaultSeed,
  creating,
  onSubmit,
  onCreated,
}: {
  projectId: string;
  defaultSeed: string;
  creating: boolean;
  onSubmit: (values: {
    project_id: string;
    seed:
      | { type: 'domain'; value: string }
      | { type: 'topics'; value: string[] };
    country_code: string;
    language: string;
    smart_mode: boolean;
    reuse_audit: boolean;
  }) => Promise<{ id: string }>;
  onCreated: (id: string) => void;
}): JSX.Element {
  const [seed, setSeed] = useState(defaultSeed);
  const [country, setCountry] = useState<string>('US');
  const [language, setLanguage] = useState<string>('en');

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    const trimmed = seed.trim();
    if (!trimmed) {
      toast.error('Provide a seed (URL or topics).');
      return;
    }
    try {
      const isUrl = /^https?:\/\//i.test(trimmed);
      const seedPayload = isUrl
        ? { type: 'domain' as const, value: trimmed }
        : {
            type: 'topics' as const,
            value: trimmed.split(',').map((t) => t.trim()).filter(Boolean),
          };
      const run = await onSubmit({
        project_id: projectId,
        seed: seedPayload,
        country_code: country,
        language,
        // Smart-seed on by default: this inline popover doesn't (yet) surface
        // a toggle, but the flag-gate on the server-side (create-keyword-run)
        // silently downgrades to legacy when the tenant's keyword_smart_seed_v1
        // flag is OFF, so this is safe. Follow-up: add a UI toggle here.
        smart_mode: true,
        reuse_audit: true,
      });
      toast.success('Keyword run started');
      onCreated(run.id);
    } catch (err) {
      const summary = summarizeApiError(err, { fallbackTitle: 'Could not start the keyword run' });
      toast.error(summary.title, { description: summary.description });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <TextField
        label="Seed"
        value={seed}
        onChange={(e) => setSeed(e.target.value)}
        placeholder="https://example.com or topic1, topic2"
        hint="URL or comma-separated topics."
      />
      <div className="grid grid-cols-2 gap-2">
        <FormField label="Country">
          <Select value={country} onValueChange={setCountry}>
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {COUNTRIES.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
        <FormField label="Language">
          <Select value={language} onValueChange={setLanguage}>
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LANGUAGES.map((l) => (
                <SelectItem key={l} value={l}>
                  {l}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
      </div>
      <Button type="submit" disabled={creating} className="w-full">
        {creating ? <Spinner className="mr-2" /> : null}
        Start run
      </Button>
    </form>
  );
}

/* ---------------- Briefs ---------------- */

function BriefsPanel({ projectId }: { projectId: string }): JSX.Element {
  const briefs = useBriefs(projectId);
  const items = briefs.data?.items ?? [];
  const counts = countByStatus(items);
  const delivered = items.filter((b) => isSuccess(b.status));
  const latest = latestOf(items);
  const failedRecent = items.filter(
    (b) =>
      isFailed(b.status) &&
      Date.now() - new Date(b.created_at).getTime() < 7 * 86400_000,
  ).length;

  return (
    <div className="space-y-4">
      <StatsRow>
        <StatTile label="Total briefs" value={counts.total} icon={Notebook} />
        <StatTile
          label="In progress"
          value={counts.inProgress}
          icon={CircleDot}
          tone={counts.inProgress > 0 ? 'info' : 'muted'}
        />
        <StatTile
          label="Delivered"
          value={delivered.length}
          icon={CheckCircle2}
          tone={delivered.length > 0 ? 'success' : 'muted'}
        />
        <StatTile
          label="Failed · 7d"
          value={failedRecent}
          icon={AlertOctagon}
          tone={failedRecent > 0 ? 'danger' : 'muted'}
        />
      </StatsRow>

      {latest ? (
        <Section>
          <SectionHeader>
            <div>
              <SectionTitle>Latest brief</SectionTitle>
              <SectionDescription>
                Target keyword, delivery channel, current status.
              </SectionDescription>
            </div>
            <SectionToolbar>
              <Button asChild variant="outline" size="sm">
                <Link to="/briefs/$briefId" params={{ briefId: latest.id }}>
                  Open brief
                </Link>
              </Button>
            </SectionToolbar>
          </SectionHeader>
          <div className="flex flex-wrap items-center gap-3">
            <StatusPill status={latest.status} />
            <span className="text-sm font-semibold">
              {latest.target_keyword || `Brief ${latest.id.slice(0, 8)}`}
            </span>
            {latest.delivery_channel ? (
              <Badge variant="outline" className="font-mono text-[10px]">
                {latest.delivery_channel}
              </Badge>
            ) : null}
            <span className="text-xs text-muted-foreground">
              created {relTime(latest.created_at)}
            </span>
          </div>
        </Section>
      ) : null}

      <Section>
        <SectionHeader>
          <div>
            <SectionTitle>All briefs</SectionTitle>
            <SectionDescription>
              Research → outline → meta variants → internal links.
            </SectionDescription>
          </div>
          <SectionToolbar>
            <BriefCreateButton
              projectId={projectId}
              buttonProps={{ size: 'sm' }}
            />
          </SectionToolbar>
        </SectionHeader>

        {briefs.isLoading ? (
          <div className="flex justify-center p-6">
            <Spinner />
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={Notebook}
            title="No briefs yet"
            description="Create a brief to generate an outline and meta variants for a target keyword."
            variant="inline"
            action={
              <BriefCreateButton
                projectId={projectId}
                buttonProps={{ size: 'sm' }}
              />
            }
          />
        ) : (
          <ul className="space-y-2">
            {sortNewest(items).map((b) => (
              <li key={b.id}>
                <Link
                  to="/briefs/$briefId"
                  params={{ briefId: b.id }}
                  className="group flex items-center justify-between gap-3 rounded-md border border-border/70 bg-card p-3 transition-colors hover:border-primary/40 hover:bg-accent/30"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <StatusPill status={b.status} />
                    <span className="truncate text-sm font-medium text-foreground group-hover:text-primary">
                      {b.target_keyword?.trim() || `Brief ${b.id.slice(0, 8)}`}
                    </span>
                  </div>
                  <div className="flex shrink-0 items-center gap-3 text-xs text-muted-foreground">
                    {b.delivery_channel ? (
                      <Badge variant="outline" className="font-mono text-[10px]">
                        {b.delivery_channel}
                      </Badge>
                    ) : null}
                    <span className="tabular">{relTime(b.created_at)}</span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}

/* ---------------- Reports ---------------- */

function ReportsPanel({ projectId }: { projectId: string }): JSX.Element {
  const reports = useReports(projectId);
  const run = useRunReport();
  const navigate = useNavigate();
  const accessToken = useAuthStore((s) => s.accessToken);
  const tenant = useAuthStore((s) => s.tenant);

  const items = reports.data?.items ?? [];
  const counts = countByStatus(items);
  const latestDelivered = latestSuccessOf(items);
  const failedRecent = items.filter(
    (r) =>
      isFailed(r.status) &&
      Date.now() - new Date(r.created_at).getTime() < 7 * 86400_000,
  ).length;
  const anomaliesLatest = latestDelivered?.anomalies?.length ?? 0;

  const runNew = async (): Promise<void> => {
    try {
      const end = new Date();
      const start = new Date(end.getTime() - 28 * 86400_000);
      const r = await run.mutateAsync({
        project_id: projectId,
        period_start: start.toISOString().slice(0, 10),
        period_end: end.toISOString().slice(0, 10),
        cadence: 'custom',
      });
      toast.success('Report queued');
      await navigate({ to: '/reports/$reportId', params: { reportId: r.id } });
    } catch (e) {
      const summary = summarizeApiError(e, { fallbackTitle: 'Could not run the report' });
      toast.error(summary.title, { description: summary.description });
    }
  };

  return (
    <div className="space-y-4">
      <StatsRow>
        <StatTile label="Total reports" value={counts.total} icon={LineChartIcon} />
        <StatTile
          label="In progress"
          value={counts.inProgress}
          icon={CircleDot}
          tone={counts.inProgress > 0 ? 'info' : 'muted'}
        />
        <StatTile
          label="Anomalies · latest"
          value={anomaliesLatest}
          icon={AlertTriangle}
          tone={anomaliesLatest > 0 ? 'warning' : 'muted'}
        />
        <StatTile
          label="Failed · 7d"
          value={failedRecent}
          icon={AlertOctagon}
          tone={failedRecent > 0 ? 'danger' : 'muted'}
        />
      </StatsRow>

      {latestDelivered ? (
        <Section>
          <SectionHeader>
            <div>
              <SectionTitle>Latest delivered report</SectionTitle>
              <SectionDescription>
                Period summary, anomalies and top recommendations.
              </SectionDescription>
            </div>
            <SectionToolbar>
              <Button asChild variant="outline" size="sm">
                <Link
                  to="/reports/$reportId"
                  params={{ reportId: latestDelivered.id }}
                >
                  Open report
                </Link>
              </Button>
            </SectionToolbar>
          </SectionHeader>
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <Badge variant="outline" className="capitalize">
              {latestDelivered.cadence}
            </Badge>
            <span>
              {latestDelivered.period_start} → {latestDelivered.period_end}
            </span>
            <span className="text-muted-foreground">
              delivered{' '}
              {relTime(
                latestDelivered.delivered_at ?? latestDelivered.created_at,
              )}
            </span>
            <Badge variant={anomaliesLatest > 0 ? 'warning' : 'secondary'}>
              {anomaliesLatest} anomal{anomaliesLatest === 1 ? 'y' : 'ies'}
            </Badge>
            <Badge variant="info">
              {latestDelivered.recommendations?.length ?? 0} recommendation
              {(latestDelivered.recommendations?.length ?? 0) === 1 ? '' : 's'}
            </Badge>
          </div>
        </Section>
      ) : null}

      <Section>
        <SectionHeader>
          <div>
            <SectionTitle>All reports</SectionTitle>
            <SectionDescription>
              Periodic SEO performance roll-ups.
            </SectionDescription>
          </div>
          <SectionToolbar>
            <Button size="sm" disabled={run.isPending} onClick={runNew}>
              {run.isPending ? (
                <Spinner className="mr-2" />
              ) : (
                <Play className="h-4 w-4 fill-current" />
              )}
              Run report
            </Button>
          </SectionToolbar>
        </SectionHeader>

        {reports.isLoading ? (
          <div className="flex justify-center p-6">
            <Spinner />
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={LineChartIcon}
            title="No reports yet"
            description="Run your first report to see traffic, rankings and anomalies."
            variant="inline"
            action={
              <Button size="sm" onClick={runNew} disabled={run.isPending}>
                <Play className="h-4 w-4 fill-current" /> Run report
              </Button>
            }
          />
        ) : (
          <ul className="space-y-2">
            {sortNewest(items).map((r) => (
              <li key={r.id} className="flex items-stretch gap-2">
                <Link
                  to="/reports/$reportId"
                  params={{ reportId: r.id }}
                  className="group flex flex-1 items-center justify-between gap-3 rounded-md border border-border/70 bg-card p-3 transition-colors hover:border-primary/40 hover:bg-accent/30"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <StatusPill status={r.status} />
                    <span className="truncate text-sm font-medium text-foreground group-hover:text-primary">
                      {r.period_start} → {r.period_end}
                    </span>
                    <Badge variant="outline" className="font-mono text-[10px] capitalize">
                      {r.cadence}
                    </Badge>
                  </div>
                  <span className="shrink-0 text-xs text-muted-foreground tabular">
                    {r.delivered_at
                      ? `delivered ${relTime(r.delivered_at)}`
                      : `created ${relTime(r.created_at)}`}
                  </span>
                </Link>
                {r.status === 'delivered' && r.delivered_at ? (
                  <button
                    type="button"
                    className="flex shrink-0 items-center justify-center rounded-md border border-border/70 bg-card px-3 transition-colors hover:border-primary/40 hover:bg-accent/30"
                    title="Download PDF"
                    onClick={async () => {
                      try {
                        await downloadAuthed({
                          url: `/v1/reports/${r.id}/report.pdf`,
                          filename: `report-${r.id.slice(0, 8)}.pdf`,
                          accessToken,
                          tenantId: tenant?.id,
                        });
                      } catch (err) {
                        const summary = summarizeApiError(err, {
                          fallbackTitle: 'Download failed',
                        });
                        toast.error(summary.title, {
                          description: summary.description,
                        });
                      }
                    }}
                  >
                    <Download className="h-3.5 w-3.5" />
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}

/* ---------------- Competitors (merged tracking + content research) ---------------- */

type CompetitorMode = 'tracking' | 'research';

function CompetitorsPanel({
  projectId,
  project,
}: {
  projectId: string;
  project: Project;
}): JSX.Element {
  const [mode, setMode] = useState<CompetitorMode>(() => readCompetitorMode());

  useEffect(() => {
    try {
      window.localStorage.setItem('seo:competitor-mode', mode);
    } catch {
      /* ignore */
    }
  }, [mode]);

  return (
    <div className="space-y-4">
      <CompetitorModeSwitcher mode={mode} onChange={setMode} />
      {mode === 'tracking' ? (
        <CompetitorsTrackingPanel projectId={projectId} project={project} />
      ) : (
        <ContentAnalysisPanel projectId={projectId} project={project} />
      )}
    </div>
  );
}

function readCompetitorMode(): CompetitorMode {
  if (typeof window === 'undefined') return 'tracking';
  try {
    const v = window.localStorage.getItem('seo:competitor-mode');
    if (v === 'research' || v === 'tracking') return v;
  } catch {
    /* ignore */
  }
  return 'tracking';
}

function CompetitorModeSwitcher({
  mode,
  onChange,
}: {
  mode: CompetitorMode;
  onChange: (m: CompetitorMode) => void;
}): JSX.Element {
  return (
    <div className="rounded-lg border border-border/70 bg-card">
      <div className="grid gap-0 md:grid-cols-2">
        <ModeCard
          active={mode === 'tracking'}
          onClick={() => onChange('tracking')}
          icon={Globe}
          title="Tracking"
          question="Did my competitor just publish something?"
          points={[
            'Watch specific domains you name',
            'Weekly sitemap diff + rank + backlinks',
            'Digest: what changed since last scan',
          ]}
          useCase="Ongoing monitoring of known rivals"
        />
        <ModeCard
          active={mode === 'research'}
          onClick={() => onChange('research')}
          icon={Sparkles}
          title="Content research"
          question="How do I outrank the top-10 for X?"
          points={[
            'Give us a keyword — no domain needed',
            'Analyze live top-N Google SERP',
            'Recommended outline + gaps',
          ]}
          useCase="One-shot before writing a piece"
        />
      </div>
    </div>
  );
}

function ModeCard({
  active,
  onClick,
  icon: Icon,
  title,
  question,
  points,
  useCase,
}: {
  active: boolean;
  onClick: () => void;
  icon: LucideIcon;
  title: string;
  question: string;
  points: string[];
  useCase: string;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'group flex flex-col gap-3 border-border/60 p-4 text-left transition-colors md:border-r md:last:border-r-0',
        active
          ? 'bg-primary/5 text-foreground'
          : 'hover:bg-accent/30 text-muted-foreground',
      )}
    >
      <div className="flex items-center gap-3">
        <div
          className={cn(
            'grid h-9 w-9 place-items-center rounded-full',
            active ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground',
          )}
        >
          <Icon className="h-4 w-4" />
        </div>
        <div className="flex-1">
          <div
            className={cn(
              'text-sm font-semibold',
              active ? 'text-foreground' : 'text-muted-foreground',
            )}
          >
            {title}
          </div>
          <div className="text-xs italic text-muted-foreground">"{question}"</div>
        </div>
        {active ? (
          <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-primary">
            Active
          </span>
        ) : null}
      </div>
      <ul className="space-y-1 pl-1 text-xs text-muted-foreground">
        {points.map((p) => (
          <li key={p} className="flex items-start gap-1.5">
            <span className="mt-0.5 text-primary/60">·</span>
            <span>{p}</span>
          </li>
        ))}
      </ul>
      <div className="mt-auto border-t border-border/50 pt-2 text-[11px] text-muted-foreground">
        Use for: <span className="text-foreground">{useCase}</span>
      </div>
    </button>
  );
}

function CompetitorsTrackingPanel({
  projectId,
  project,
}: {
  projectId: string;
  project: Project;
}): JSX.Element {
  const competitors = useCompetitors(projectId);
  const runs = useCompetitorRuns(projectId);
  const qc = useQueryClient();

  const items = competitors.data?.items ?? [];
  const runRows = runs.data?.items ?? [];
  const runCounts = countByStatus(runRows);
  const latestRun = latestOf(runRows);
  const failedRecent = runRows.filter(
    (r) =>
      isFailed(r.status) &&
      Date.now() - new Date(r.created_at).getTime() < 7 * 86400_000,
  ).length;

  const handleDelete = async (id: string, domain: string): Promise<void> => {
    try {
      await apiDelete(`/v1/competitors/${id}`);
      void qc.invalidateQueries({ queryKey: ['competitors'] });
      toast.success('Competitor removed', { description: domain });
    } catch (e) {
      const summary = summarizeApiError(e, { fallbackTitle: 'Could not remove competitor' });
      toast.error(summary.title, { description: summary.description });
    }
  };

  return (
    <div className="space-y-4">
      <StatsRow>
        <StatTile label="Tracked" value={items.length} icon={Globe} />
        <StatTile label="Total scans" value={runCounts.total} icon={Zap} />
        <StatTile
          label="In progress"
          value={runCounts.inProgress}
          icon={CircleDot}
          tone={runCounts.inProgress > 0 ? 'info' : 'muted'}
        />
        <StatTile
          label="Failed · 7d"
          value={failedRecent}
          icon={AlertOctagon}
          tone={failedRecent > 0 ? 'danger' : 'muted'}
          hint={latestRun ? `last ${relTime(latestRun.created_at)}` : undefined}
        />
      </StatsRow>

      <Section>
        <SectionHeader>
          <div>
            <SectionTitle className="flex items-center gap-2">
              <Globe className="h-4 w-4 text-muted-foreground" />
              Tracked competitors
            </SectionTitle>
            <SectionDescription>
              Domains we'll diff and classify on every scan.
            </SectionDescription>
          </div>
          <SectionToolbar>
            <CompetitorRunButton
              projectId={projectId}
              competitors={items}
              buttonProps={{ variant: 'outline', size: 'sm' }}
            />
            <CompetitorCreateButton
              projects={[project]}
              defaultProjectId={projectId}
              buttonProps={{ size: 'sm' }}
            />
          </SectionToolbar>
        </SectionHeader>

        {competitors.isLoading ? (
          <div className="flex justify-center p-6">
            <Spinner />
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={Globe}
            title="No competitors tracked"
            description="Add a competitor domain to track sitemap diffs, new page classification and rank gaps."
            variant="inline"
            action={
              <CompetitorCreateButton
                projects={[project]}
                defaultProjectId={projectId}
                label="Add your first competitor"
                buttonProps={{ size: 'sm' }}
              />
            }
          />
        ) : (
          <ul className="space-y-2">
            {items.map((c) => (
              <li
                key={c.id}
                className="flex items-center justify-between rounded-md border border-border/70 bg-card p-3"
              >
                <div className="min-w-0">
                  <div className="font-mono text-sm">{c.domain}</div>
                  {c.label ? (
                    <div className="text-xs text-muted-foreground">
                      {c.label}
                    </div>
                  ) : null}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={`Stop tracking ${c.domain}`}
                  onClick={() => handleDelete(c.id, c.domain)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section>
        <SectionHeader>
          <div>
            <SectionTitle className="flex items-center gap-2">
              <Zap className="h-4 w-4 text-muted-foreground" />
              All scans
            </SectionTitle>
            <SectionDescription>
              Each scan diffs sitemaps and produces a digest.
            </SectionDescription>
          </div>
        </SectionHeader>
        {runs.isLoading ? (
          <div className="flex justify-center p-6">
            <Spinner />
          </div>
        ) : runRows.length === 0 ? (
          <EmptyState
            icon={Zap}
            title="No scans yet"
            description={
              items.length === 0
                ? 'Add a competitor first, then click Run scan.'
                : 'Click Run scan above to start one.'
            }
            variant="inline"
          />
        ) : (
          <ul className="space-y-2">
            {sortNewest(runRows).map((r) => (
              <li key={r.id}>
                <Link
                  to="/competitors/$runId"
                  params={{ runId: r.id }}
                  className="group flex items-center justify-between gap-3 rounded-md border border-border/70 bg-card p-3 transition-colors hover:border-primary/40 hover:bg-accent/30"
                >
                  <div>
                    <div className="font-mono text-sm group-hover:text-primary">
                      run-{r.id.slice(0, 8)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {r.competitor_ids.length} competitor
                      {r.competitor_ids.length === 1 ? '' : 's'} ·{' '}
                      {relTime(r.created_at)}
                    </div>
                  </div>
                  <StatusPill status={r.status} />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}

/* ---------------- Content analysis (A5 v2) ---------------- */

function ContentAnalysisPanel({
  projectId,
  project,
}: {
  projectId: string;
  project: Project;
}): JSX.Element {
  const analyses = useCompetitorAnalyses(projectId);
  const rows = analyses.data?.items ?? [];
  const counts = countByStatus(rows);
  const activeAnalysis = rows.find((r) => isInProgress(r.status));

  return (
    <div className="space-y-4">
      <StatsRow>
        <StatTile label="Analyses" value={rows.length} icon={Search} />
        <StatTile
          label="In progress"
          value={counts.inProgress}
          icon={CircleDot}
          tone={counts.inProgress > 0 ? 'info' : 'muted'}
          hint={activeAnalysis ? activeAnalysis.keyword : undefined}
        />
        <StatTile
          label="Completed"
          value={counts.succeeded}
          icon={Sparkles}
          tone={counts.succeeded > 0 ? 'success' : 'muted'}
        />
        <StatTile
          label="Failed"
          value={counts.failed}
          icon={AlertOctagon}
          tone={counts.failed > 0 ? 'danger' : 'muted'}
        />
      </StatsRow>

      <Section>
        <SectionHeader>
          <div>
            <SectionTitle className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-muted-foreground" />
              Analyses
            </SectionTitle>
            <SectionDescription>
              One analysis per keyword. Each produces a SERP archetype +
              recommended outline you can hand to a writer.
            </SectionDescription>
          </div>
          <SectionToolbar>
            <CompetitorAnalysisCreateButton
              projects={[project]}
              defaultProjectId={projectId}
              buttonProps={{ size: 'sm' }}
            />
          </SectionToolbar>
        </SectionHeader>

        {analyses.isLoading ? (
          <div className="flex justify-center p-6">
            <Spinner />
          </div>
        ) : rows.length === 0 ? (
          <ContentAnalysisEmptyState project={project} projectId={projectId} />
        ) : (
          <ul className="space-y-2">
            {rows.map((a) => (
              <ContentAnalysisRow key={a.id} analysis={a} />
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}

function ContentAnalysisEmptyState({
  project,
  projectId,
}: {
  project: Project;
  projectId: string;
}): JSX.Element {
  return (
    <div className="grid gap-4 rounded-lg border border-dashed border-border/70 bg-muted/20 p-6 md:grid-cols-[1fr_auto]">
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-full bg-primary/10 text-primary">
            <Sparkles className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-base font-semibold">
              Turn a target keyword into a battle plan
            </h3>
            <p className="text-sm text-muted-foreground">
              This is the research pass you run before writing. Not weekly
              monitoring — that's the Competitors tab.
            </p>
          </div>
        </div>
        <ul className="grid gap-2 pl-1 text-sm text-muted-foreground sm:grid-cols-2">
          <li className="flex items-start gap-2">
            <ListTree className="mt-0.5 h-3.5 w-3.5 text-primary/70" />
            <span>SERP archetype: what Google is rewarding on this query</span>
          </li>
          <li className="flex items-start gap-2">
            <Target className="mt-0.5 h-3.5 w-3.5 text-primary/70" />
            <span>Recommended angle: what makes your version different</span>
          </li>
          <li className="flex items-start gap-2">
            <ListTree className="mt-0.5 h-3.5 w-3.5 text-primary/70" />
            <span>4–8 outline H2s tagged must_have / gap / differentiator</span>
          </li>
          <li className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 text-primary/70" />
            <span>Content gaps + per-page scorecards</span>
          </li>
        </ul>
      </div>
      <div className="flex items-end justify-end">
        <CompetitorAnalysisCreateButton
          projects={[project]}
          defaultProjectId={projectId}
        />
      </div>
    </div>
  );
}

function ContentAnalysisRow({ analysis }: { analysis: CompetitorAnalysis }): JSX.Element {
  const done = isSuccess(analysis.status);
  const failed = isFailed(analysis.status);
  const inProg = isInProgress(analysis.status);
  const outlines = Array.isArray(analysis.outline_hints)
    ? analysis.outline_hints.length
    : 0;

  return (
    <li>
      <Link
        to="/competitor-analyses/$analysisId"
        params={{ analysisId: analysis.id }}
        className="group grid gap-3 rounded-md border border-border/70 bg-card p-3 transition-colors hover:border-primary/40 hover:bg-accent/30 sm:grid-cols-[minmax(0,1fr)_auto]"
      >
        <div className="min-w-0 space-y-2">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-medium group-hover:text-primary">
              {analysis.keyword}
            </span>
            <span className="rounded border border-border/70 px-1.5 py-0 text-[10px] uppercase text-muted-foreground">
              {analysis.language}-{analysis.country}
            </span>
            <span className="rounded border border-border/70 px-1.5 py-0 text-[10px] uppercase text-muted-foreground">
              {analysis.serp_source ?? 'no source'}
            </span>
          </div>
          {done ? (
            <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
              <span>
                <span className="tabular font-medium text-foreground">
                  {analysis.competitors_included ?? 0}
                </span>{' '}
                pages analyzed
              </span>
              <span>
                <span className="tabular font-medium text-foreground">
                  {analysis.clusters_found ?? 0}
                </span>{' '}
                clusters
              </span>
              <span>
                <span className="tabular font-medium text-foreground">
                  {analysis.gaps_found ?? 0}
                </span>{' '}
                gaps
              </span>
              <span>
                <span className="tabular font-medium text-foreground">{outlines}</span>{' '}
                outline H2s
              </span>
            </div>
          ) : inProg ? (
            <div className="text-xs text-muted-foreground">
              Phase:{' '}
              <span className="font-medium text-foreground">
                {humanStatus(String(analysis.progress?.['phase'] ?? analysis.status))}
              </span>{' '}
              · started {relTime(analysis.started_at ?? analysis.created_at)}
            </div>
          ) : failed ? (
            <div className="text-xs text-destructive">
              {analysis.failure_reason ?? 'Failed — click to see details.'}
            </div>
          ) : (
            <div className="text-xs text-muted-foreground">
              Queued · {relTime(analysis.created_at)}
            </div>
          )}
        </div>
        <div className="flex items-center gap-3 sm:justify-self-end">
          {done && analysis.recommended_angle ? (
            <span className="hidden max-w-xs truncate text-xs italic text-muted-foreground md:inline">
              "{analysis.recommended_angle.slice(0, 80)}
              {analysis.recommended_angle.length > 80 ? '…' : ''}"
            </span>
          ) : null}
          <StatusPill status={analysis.status} />
        </div>
      </Link>
    </li>
  );
}

/* ---------------- Calendars ---------------- */

function CalendarsPanel({
  projectId,
  project,
}: {
  projectId: string;
  project: Project;
}): JSX.Element {
  const cals = useCalendars(projectId);
  const items = cals.data?.items ?? [];
  const counts = countByStatus(items);
  const latest = latestOf(items);
  const failedRecent = items.filter(
    (c) =>
      isFailed(c.status) &&
      Date.now() - new Date(c.created_at).getTime() < 7 * 86400_000,
  ).length;

  return (
    <div className="space-y-4">
      <StatsRow>
        <StatTile label="Total calendars" value={counts.total} icon={CalendarDays} />
        <StatTile
          label="In progress"
          value={counts.inProgress}
          icon={CircleDot}
          tone={counts.inProgress > 0 ? 'info' : 'muted'}
        />
        <StatTile
          label="Latest period"
          value={latest ? `${latest.period_months}mo` : '—'}
          icon={Clock}
          hint={
            latest
              ? `${latest.capacity_per_month}/mo · ${relTime(latest.created_at)}`
              : 'no calendars yet'
          }
        />
        <StatTile
          label="Failed · 7d"
          value={failedRecent}
          icon={AlertOctagon}
          tone={failedRecent > 0 ? 'danger' : 'muted'}
        />
      </StatsRow>

      <Section>
        <SectionHeader>
          <div>
            <SectionTitle>All calendars</SectionTitle>
            <SectionDescription>
              Pillar + spoke + refresh schedules.
            </SectionDescription>
          </div>
          <SectionToolbar>
            <CalendarCreateButton
              projects={[project]}
              defaultProjectId={projectId}
              buttonProps={{ size: 'sm' }}
            />
          </SectionToolbar>
        </SectionHeader>

        {cals.isLoading ? (
          <div className="flex justify-center p-6">
            <Spinner />
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={CalendarDays}
            title="No calendars yet"
            description="Generate one to see how the strategy agent sequences your content."
            variant="inline"
            action={
              <CalendarCreateButton
                projects={[project]}
                defaultProjectId={projectId}
                label="Generate your first calendar"
                buttonProps={{ size: 'sm' }}
              />
            }
          />
        ) : (
          <ul className="space-y-2">
            {sortNewest(items).map((c: ContentCalendar) => (
              <li key={c.id}>
                <Link
                  to="/calendars/$calendarId"
                  params={{ calendarId: c.id }}
                  className="group flex items-center justify-between gap-3 rounded-md border border-border/70 bg-card p-3 transition-colors hover:border-primary/40 hover:bg-accent/30"
                >
                  <div>
                    <div className="font-mono text-sm group-hover:text-primary">
                      cal-{c.id.slice(0, 8)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {c.period_months} month
                      {c.period_months === 1 ? '' : 's'} ·{' '}
                      {c.capacity_per_month}/mo · created{' '}
                      {relTime(c.created_at)}
                    </div>
                  </div>
                  <StatusPill status={c.status} />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}

/* ---------------- Campaigns ---------------- */

function CampaignsPanel({
  projectId,
  project,
}: {
  projectId: string;
  project: Project;
}): JSX.Element {
  const campaigns = useCampaigns(projectId);
  const items = campaigns.data?.items ?? [];
  const counts = countByStatus(items);
  const pending = items.filter((c) => isNeedsReview(c.status)).length;
  const latest = latestOf(items);
  const failedRecent = items.filter(
    (c) =>
      isFailed(c.status) &&
      Date.now() - new Date(c.created_at).getTime() < 7 * 86400_000,
  ).length;

  return (
    <div className="space-y-4">
      <StatsRow>
        <StatTile label="Total campaigns" value={counts.total} icon={Target} />
        <StatTile
          label="Running"
          value={counts.inProgress}
          icon={CircleDot}
          tone={counts.inProgress > 0 ? 'info' : 'muted'}
        />
        <StatTile
          label="Awaiting approval"
          value={pending}
          icon={AlertTriangle}
          tone={pending > 0 ? 'warning' : 'muted'}
        />
        <StatTile
          label="Failed · 7d"
          value={failedRecent}
          icon={AlertOctagon}
          tone={failedRecent > 0 ? 'danger' : 'muted'}
        />
      </StatsRow>

      {latest ? (
        <Section>
          <SectionHeader>
            <div>
              <SectionTitle>Latest campaign</SectionTitle>
              <SectionDescription>
                Goal, horizon and current step.
              </SectionDescription>
            </div>
            <SectionToolbar>
              <Button asChild variant="outline" size="sm">
                <Link
                  to="/campaigns/$campaignId"
                  params={{ campaignId: latest.id }}
                >
                  Open campaign
                </Link>
              </Button>
            </SectionToolbar>
          </SectionHeader>
          <div className="space-y-2 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <StatusPill status={latest.status} />
              <Badge variant="outline" className="font-mono text-[10px]">
                {latest.horizon_days}-day horizon
              </Badge>
              <span className="text-xs text-muted-foreground">
                created {relTime(latest.created_at)}
              </span>
            </div>
            <p className="line-clamp-2 text-sm text-foreground">{latest.goal}</p>
          </div>
        </Section>
      ) : null}

      <Section>
        <SectionHeader>
          <div>
            <SectionTitle>All campaigns</SectionTitle>
            <SectionDescription>
              Each campaign decomposes a goal into a DAG of specialist agents.
            </SectionDescription>
          </div>
          <SectionToolbar>
            <CampaignCreateButton
              projects={[project]}
              defaultProjectId={projectId}
              buttonProps={{ size: 'sm' }}
            />
          </SectionToolbar>
        </SectionHeader>

        {campaigns.isLoading ? (
          <div className="flex justify-center p-6">
            <Spinner />
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={Target}
            title="No campaigns yet"
            description="Describe a goal and the orchestrator will plan + execute the right agent mix."
            variant="inline"
            action={
              <CampaignCreateButton
                projects={[project]}
                defaultProjectId={projectId}
                label="Run your first campaign"
                buttonProps={{ size: 'sm' }}
              />
            }
          />
        ) : (
          <ul className="space-y-2">
            {sortNewest(items).map((c) => (
              <li key={c.id}>
                <Link
                  to="/campaigns/$campaignId"
                  params={{ campaignId: c.id }}
                  className="group flex items-start justify-between gap-3 rounded-md border border-border/70 bg-card p-3 transition-colors hover:border-primary/40 hover:bg-accent/30"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-sm">
                      <Sparkles className="h-4 w-4 text-primary" />
                      <span className="font-mono text-xs text-muted-foreground">
                        camp-{c.id.slice(0, 8)}
                      </span>
                    </div>
                    <div className="mt-1 line-clamp-2 text-sm text-foreground group-hover:text-primary">
                      {c.goal}
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {c.horizon_days ? `${c.horizon_days}-day horizon · ` : ''}
                      created {relTime(c.created_at)}
                    </div>
                  </div>
                  <StatusPill status={c.status} />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}

/* ---------------- Forecasts ---------------- */

function ForecastsPanel({
  projectId,
  project,
}: {
  projectId: string;
  project: Project;
}): JSX.Element {
  const forecasts = useForecasts(projectId);
  const items = forecasts.data?.items ?? [];
  const counts = countByStatus(items);
  const latest = latestOf(items);
  const failedRecent = items.filter(
    (f) =>
      isFailed(f.status) &&
      Date.now() - new Date(f.created_at).getTime() < 7 * 86400_000,
  ).length;

  return (
    <div className="space-y-4">
      <StatsRow>
        <StatTile label="Total forecasts" value={counts.total} icon={TrendingUp} />
        <StatTile
          label="In progress"
          value={counts.inProgress}
          icon={CircleDot}
          tone={counts.inProgress > 0 ? 'info' : 'muted'}
        />
        <StatTile
          label="Latest horizon"
          value={latest ? `${latest.horizon_days}d` : '—'}
          icon={Clock}
          hint={latest ? relTime(latest.created_at) : 'none yet'}
        />
        <StatTile
          label="Failed · 7d"
          value={failedRecent}
          icon={AlertOctagon}
          tone={failedRecent > 0 ? 'danger' : 'muted'}
        />
      </StatsRow>

      <Section>
        <SectionHeader>
          <div>
            <SectionTitle>All forecasts</SectionTitle>
            <SectionDescription>
              Baseline vs. scenario projections with confidence bands.
            </SectionDescription>
          </div>
          <SectionToolbar>
            <ForecastCreateButton
              projects={[project]}
              defaultProjectId={projectId}
              buttonProps={{ size: 'sm' }}
            />
          </SectionToolbar>
        </SectionHeader>

        {forecasts.isLoading ? (
          <div className="flex justify-center p-6">
            <Spinner />
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={TrendingUp}
            title="No forecasts yet"
            description="Generate a forecast to see baseline vs scenarios with confidence bands."
            variant="inline"
            action={
              <ForecastCreateButton
                projects={[project]}
                defaultProjectId={projectId}
                label="Generate your first forecast"
                buttonProps={{ size: 'sm' }}
              />
            }
          />
        ) : (
          <ul className="space-y-2">
            {sortNewest(items).map((f) => (
              <li key={f.id}>
                <Link
                  to="/forecasts/$forecastId"
                  params={{ forecastId: f.id }}
                  className="group flex items-center justify-between gap-3 rounded-md border border-border/70 bg-card p-3 transition-colors hover:border-primary/40 hover:bg-accent/30"
                >
                  <div>
                    <div className="font-mono text-sm group-hover:text-primary">
                      fc-{f.id.slice(0, 8)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {f.horizon_days}-day horizon · created{' '}
                      {relTime(f.created_at)}
                    </div>
                  </div>
                  <StatusPill status={f.status} />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}

/* ---------------- Settings ---------------- */

interface PreferredSourceRow {
  name: string;
  url: string;
  topic_tags_input: string;
}

function toPreferredSourceRows(
  value: unknown,
): PreferredSourceRow[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry): PreferredSourceRow[] => {
    if (!entry || typeof entry !== 'object') return [];
    const e = entry as Record<string, unknown>;
    const name = typeof e['name'] === 'string' ? e['name'] : '';
    const url = typeof e['url'] === 'string' ? e['url'] : '';
    const tags = Array.isArray(e['topic_tags'])
      ? (e['topic_tags'] as unknown[]).filter(
          (t): t is string => typeof t === 'string',
        )
      : [];
    return [{ name, url, topic_tags_input: tags.join(', ') }];
  });
}

function rowsToPreferredSources(
  rows: PreferredSourceRow[],
): { name: string; url: string; topic_tags: string[] }[] {
  return rows
    .map((r) => ({
      name: r.name.trim(),
      url: r.url.trim(),
      topic_tags: r.topic_tags_input
        .split(',')
        .map((t) => t.trim())
        .filter((t) => t.length > 0),
    }))
    .filter((r) => r.name.length > 0 && r.url.length > 0);
}

function SettingsPanel({ project }: { project: Project }): JSX.Element {
  const update = useUpdateProject(project.id);
  const [brandPositioning, setBrandPositioning] = useState<string>(
    project.brand_positioning ?? '',
  );
  const [proprietaryAssets, setProprietaryAssets] = useState<string>(
    project.proprietary_assets ?? '',
  );
  const [preferredSources, setPreferredSources] = useState<PreferredSourceRow[]>(
    toPreferredSourceRows(project.default_preferred_sources),
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setBrandPositioning(project.brand_positioning ?? '');
  }, [project.brand_positioning]);

  useEffect(() => {
    setProprietaryAssets(project.proprietary_assets ?? '');
  }, [project.proprietary_assets]);

  useEffect(() => {
    setPreferredSources(toPreferredSourceRows(project.default_preferred_sources));
  }, [project.default_preferred_sources]);

  const initialPositioning = (project.brand_positioning ?? '').trim();
  const initialAssets = (project.proprietary_assets ?? '').trim();
  const trimmedPositioning = brandPositioning.trim();
  const trimmedAssets = proprietaryAssets.trim();

  const cleanedSources = rowsToPreferredSources(preferredSources);
  const initialSources = Array.isArray(project.default_preferred_sources)
    ? (project.default_preferred_sources as unknown[])
    : [];
  const sourcesDirty =
    JSON.stringify(cleanedSources) !== JSON.stringify(initialSources);

  const positioningTooLong = trimmedPositioning.length > 1000;
  const assetsTooLong = trimmedAssets.length > 2000;

  const dirty =
    trimmedPositioning !== initialPositioning ||
    trimmedAssets !== initialAssets ||
    sourcesDirty;

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (positioningTooLong) {
      setError('Keep brand positioning under 1000 characters.');
      return;
    }
    if (assetsTooLong) {
      setError('Keep proprietary assets under 2000 characters.');
      return;
    }
    setError(null);
    try {
      await update.mutateAsync({
        brand_positioning: trimmedPositioning === '' ? null : trimmedPositioning,
        proprietary_assets: trimmedAssets === '' ? null : trimmedAssets,
        default_preferred_sources: cleanedSources,
      });
      toast.success('Project settings saved');
    } catch (err) {
      const summary = summarizeApiError(err, {
        fallbackTitle: 'Could not save project settings',
        fieldLabels: {
          brand_positioning: 'Brand positioning',
          proprietary_assets: 'Proprietary assets',
          default_preferred_sources: 'Preferred sources',
        },
      });
      toast.error(summary.title, { description: summary.description });
    }
  };

  const addSource = (): void => {
    setPreferredSources((prev) => [
      ...prev,
      { name: '', url: '', topic_tags_input: '' },
    ]);
  };
  const updateSource = (
    idx: number,
    patch: Partial<PreferredSourceRow>,
  ): void => {
    setPreferredSources((prev) =>
      prev.map((row, i) => (i === idx ? { ...row, ...patch } : row)),
    );
  };
  const removeSource = (idx: number): void => {
    setPreferredSources((prev) => prev.filter((_, i) => i !== idx));
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <Section>
        <SectionHeader>
          <div>
            <SectionTitle>Brand positioning</SectionTitle>
            <SectionDescription>
              Feeds into content brief generation so the writer anchors on your
              product, not a competitor's.
            </SectionDescription>
          </div>
        </SectionHeader>
        <TextAreaField
          label="Brand positioning"
          hideLabel
          placeholder="e.g. Name.ai is a domain-name discovery tool for founders picking their first brand. We don't sell hosting, page builders, or logo design."
          hint="1-3 sentences describing what your brand sells, your unique wedge, and what you don't do. Prevents brief prompts from anchoring on competitor positioning."
          rows={5}
          maxLength={1000}
          value={brandPositioning}
          onChange={(e) => setBrandPositioning(e.target.value)}
        />
        <div className="mt-2 text-xs text-muted-foreground">
          {trimmedPositioning.length} / 1000
        </div>
      </Section>

      <Section>
        <SectionHeader>
          <div>
            <SectionTitle>Proprietary assets</SectionTitle>
            <SectionDescription>
              Any proprietary data — transaction data, research, in-house case
              studies — the AI writer can cite.
            </SectionDescription>
          </div>
        </SectionHeader>
        <TextAreaField
          label="Proprietary assets"
          hideLabel
          placeholder="e.g. 12 years of aftermarket domain transaction data (median sale price by TLD, escrow flow, buyer intent surveys); interviews with 40+ brokers."
          hint="Referenced by the DRAFT prompt when an EEAT / originality gap is detected in the competitor analysis."
          rows={5}
          maxLength={2000}
          value={proprietaryAssets}
          onChange={(e) => setProprietaryAssets(e.target.value)}
        />
        <div className="mt-2 text-xs text-muted-foreground">
          {trimmedAssets.length} / 2000
        </div>
      </Section>

      <Section>
        <SectionHeader>
          <div>
            <SectionTitle>Default preferred sources</SectionTitle>
            <SectionDescription>
              Named authorities the brief prompt can cite. Copied into every new
              brief unless the creator overrides.
            </SectionDescription>
          </div>
          <SectionToolbar>
            <Button type="button" size="sm" variant="outline" onClick={addSource}>
              Add source
            </Button>
          </SectionToolbar>
        </SectionHeader>
        {preferredSources.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No sources yet. Add authorities you want cited in briefs — e.g.
            ICANN, Verisign DNIB.
          </p>
        ) : (
          <ul className="space-y-3">
            {preferredSources.map((row, idx) => (
              <li
                key={idx}
                className="grid grid-cols-1 gap-2 rounded-md border border-border/70 bg-card p-3 md:grid-cols-[1fr,1fr,1fr,auto]"
              >
                <TextField
                  label="Name"
                  placeholder="ICANN"
                  value={row.name}
                  onChange={(e) => updateSource(idx, { name: e.target.value })}
                />
                <TextField
                  label="URL"
                  placeholder="https://www.icann.org/resources"
                  value={row.url}
                  onChange={(e) => updateSource(idx, { url: e.target.value })}
                />
                <TextField
                  label="Topic tags"
                  hint="Comma-separated"
                  placeholder="policy, domains"
                  value={row.topic_tags_input}
                  onChange={(e) =>
                    updateSource(idx, { topic_tags_input: e.target.value })
                  }
                />
                <div className="flex items-end">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label="Remove source"
                    onClick={() => removeSource(idx)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
        <p className="mt-3 text-xs text-muted-foreground">
          Author roster is API-editable — contact your engineer to add authors.
        </p>
      </Section>

      {error ? (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : null}

      <div className="flex items-center justify-end">
        <Button
          type="submit"
          size="sm"
          disabled={
            !dirty ||
            positioningTooLong ||
            assetsTooLong ||
            update.isPending
          }
        >
          {update.isPending ? <Spinner className="mr-2" /> : null}
          Save changes
        </Button>
      </div>
    </form>
  );
}
